import db from "../../../../database";
import dayjs from "dayjs";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { upsertTableStatus, cancelFBOrder, upsertTableReservation, upsertFBOrder, getFBOrder } from "../../../../services/index";
import { v4 as uuidv4 } from "uuid";
import { generateFormattedKOTs } from "../../../../utils/generateKOTs";
import useAuthStore from "../../../../stores/useAuthStore";
import useCartStore from "../../../../stores/useCartStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";

const SyncData = async (item, type, setLoading, status, IPOrder, cart) => {
  const serverUrl = process.env.REACT_APP_serverUrl;
  const authHeaders = getOAuthHeaders();
  const generateMergeId = () => uuidv4().replace(/-/g, "").toUpperCase();
  let token;
  if (authHeaders) {
    token = authHeaders.access_token;
  }
  const tillData = useAuthStore.getState().tillData;
  const username = useAuthStore.getState().username;
  const salesReps = tillData?.tillAccess?.csBunit?.salesRep || [];
  const matchedRep = salesReps.find((rep) => rep.name === username);
  if (type === "upsertTableStatus") {
    let mergeString = "[]";
    if (item.merge && item.merge.length > 0) {
      mergeString =
        "[" +
        item.merge
          .map(
            (m) => `{
            fbMergeId:"${m.fbMergeId || generateMergeId()}"
            fbMTableId:"${m.fbMTableId || m.tableId}"
            fbMergeReason:"${m.fbMergeReason || m.reason || ""}"
          }`
          )
          .join(",") +
        "]";
    }

    let splitTablesString = "[]";
    if (item.splitTables && item.splitTables.length > 0) {
      splitTablesString =
        "[" +
        item.splitTables
          .map(
            (s) => `{
        splitTable:"${s.splitTable || ""}"
        status:"${s.status || "OPN"}"
        guests:${s.guests || 0}
        guestName:"${s.guestName || ""}"
        guestType:"${s.guestType || ""}"
        ${s.salesRepId ? `salesRepId:"${s.salesRepId}"` : ""}
      }`
          )
          .join(",") +
        "]";
    }

    const salesRepIdToSend = item.waiter || matchedRep?.salesRepresentId || null;

    const paramsInput = {
      query: `mutation {
                  upsertTableStatus(tableStatus: {
                    cSBunitID: "${tillData.tillAccess.csBunit.csBunitId}"
                    fbtableStatusId: ${item.fbtableStatusId ? `"${item.fbtableStatusId}"` : null}
                    fbTableId: "${item.cwrFbTableId}"
                    fbSectionId: "${item.cwrFbsectionId}"
                    status: "${item.statusType}"
                    guests: ${item.noOfPersons || 0}
                    guestName: "${item.guestName || ""}"
                    guestType: "${item.guestType || ""}"
                    referredBy: "${item.referredBy || ""}"
                    salesRepId: ${salesRepIdToSend ? `"${salesRepIdToSend}"` : null}
                    merge: ${mergeString}
                    splitTables: ${splitTablesString}
                    ${item.splitTable ? `splitTable: "${item.splitTable}"` : ""}
                   }) { 
                status
                message   
                }
                }`,
    };
    // Optimistic write: persist new status to local DB before the API call.
    // If the API fails, the local DB has the correct status and the sync retry
    // will re-attempt the server call on the next syncRestaurantTables run.
    try {
      let allTableData = await db.tableData.toArray();
      let optimistic = allTableData.map((ele) => {
        if (ele.cwrFbTableId === item.cwrFbTableId) {
          const updated = {
            ...ele,
            statusType: item.statusType,
            color: item.statusType === "OCU" ? "#bc4749" : item.statusType === "RES" ? "#f2e8cf" : "#a7c957",
            tabbleSync: "N",
          };
          if (item.statusType === "OPN") updated.ordered = "N";
          if (item.noOfPersons !== undefined) updated.noOfPersons = item.noOfPersons;
          if (item.guestName !== undefined) updated.guestName = item.guestName;
          if (item.guestType !== undefined) updated.guestType = item.guestType;
          if (item.referredBy !== undefined) updated.referredBy = item.referredBy;
          if (item.waiter !== undefined) updated.waiter = item.waiter;
          if (item.merge !== undefined) updated.merge = item.merge;
          if (item.splitTables !== undefined) updated.splitTables = item.splitTables;
          return updated;
        }
        return ele;
      });
      await db.tableData.clear();
      await db.tableData.bulkPut(optimistic);
    } catch (dbErr) {
      console.debug("SyncData optimistic DB write failed:", dbErr);
    }

    try {
      const response = await upsertTableStatus(paramsInput);
      const status = response?.data?.data?.upsertTableStatus?.status;

      if (status === "200") {
        let allTableData = await db.tableData.toArray();
        let data = allTableData.map((ele) =>
          ele.cwrFbTableId === item.cwrFbTableId ? { ...ele, tabbleSync: "Y" } : ele
        );
        await db.tableData.clear();
        await db.tableData.bulkPut(data);
        return { success: true };
      }

      console.warn("SyncData upsertTableStatus status not 200:", status);
      return { success: false };
    } catch (err) {
      console.error("SyncData upsertTableStatus error:", err);
      return { success: false, error: err };
    }
  } else if (type === "cancelItem") {
    const cancelItemsData = {
      query: `mutation {
                cancelFBOrder(fbOrder: {
                  fbOrderId:"${item.fbOrderId}"
                  lines:[${item.cancelItems}]
                 }) { 
              status
              message   
              }
              }`,
    };
    await cancelFBOrder(cancelItemsData);
  } else if (type === "reservation") {
    const fbTableIdValue = item.cwrFbTableId || item.tableId || null;
    const fbSectionIdValue = item.cwrFbsectionId || item.sectionId || null;
    const paramsInput = {
      query: `mutation {
              upsertTableReservation(tableReservation: {
                cSBunitID: "${tillData.tillAccess.csBunit.csBunitId}"
                fbTableReservationId: "${item.uniqueId ? item.uniqueId : item.id}"
                fbTableId: ${fbTableIdValue ? `"${fbTableIdValue}"` : null}
                fbSectionId: ${fbSectionIdValue ? `"${fbSectionIdValue}"` : null}
                customerName: "${item.name === undefined ? "" : item.name}"
                noOfPeople: "${item.noOfPersons ? item.noOfPersons : item.guests}"
                ${item.mobile || item.phone ? `contactNo: "${item.mobile || item.phone}"` : ""},
                ${item.guestType ? `guestType: "${item.guestType}"` : ""},
                ${item.referredBy ? `referredBy: "${item.referredBy}"` : ""},
                ${item.status ? `status: "${item.status}"` : ""},
                 resDate: "${typeof item.time === "string" && item.time.includes("/") ? dayjs(item.time, "DD/MM/YYYY, hh:mm A").format("YYYY-MM-DD") : dayjs(item.time).format("YYYY-MM-DD")
        }"

    resTime: "${typeof item.time === "string" && item.time.includes("/")
          ? dayjs(item.time, "DD/MM/YYYY, hh:mm A").format("YYYY-MM-DD HH:mm:ss")
          : dayjs(item.time).format("YYYY-MM-DD HH:mm:ss")
        }"
               }) { 
            status
            message   
            }
            }`,
    };
    await upsertTableReservation(paramsInput);
  } else if (type === "upsertFbOrder") {
    const cartDetails = useCartStore.getState().cart;
    const tableName = useRestaurantStore.getState().tableName;
    const dineIn = useRestaurantStore.getState().dineIn;

    const cartItems = { ...cartDetails, tableNo: tableName?.name ? tableName?.name : "" };
    const cartMetaValue = JSON.stringify(JSON.stringify(cartItems));
    const fbOrderMetaId = uuidv4().replace(/-/g, "").toUpperCase();
    const fbOrders = JSON.parse(localStorage.getItem("getFBOrderData") || "[]");

    let tempResponse = null;

    // Use item directly — no DB query needed
    const orders = Array.isArray(item) ? item : [item];

    if (orders.length > 0) {
      for (let i = 0; i < orders.length; i++) {
        const o = orders[i];

          let metaString = "[]";
          if (IPOrder && o.isCanceled !== "Y") {
            metaString = `[
            {
              fbOrderMetaId: "${fbOrderMetaId}"
              key: "cartDetails"
              value: ${cartMetaValue}
            }
          ]`;
          }

          const filteredOrders = Array.isArray(fbOrders)
            ? fbOrders.filter(
              (fo) => fo.fbTableId === o.cwrFbTableId || fo.fbTableId === o.fbTableId
            )
            : [];

          let selectedProductIds = [];
          if (Array.isArray(o.lines)) {
            selectedProductIds = o.lines
              .filter(
                (line) =>
                  line &&
                  line.fbOrderLineId &&
                  line.fbOrderLineId !== "undefined" &&
                  line.mPoductId &&
                  line.mPoductId !== "undefined" &&
                  line.csUomId &&
                  line.csUomId !== "undefined" &&
                  line.csTaxId &&
                  line.csTaxId !== "undefined"
              )
              .map((line) => line.lineId);
          }

          let mergeString = "[]";
          if (o.mergeTables && o.mergeTables.length > 0) {
            mergeString =
              "[" +
              o.mergeTables
                .map(
                  (m) => `{
                  fbMergeId:"${generateMergeId()}"
                  fbMergeReason:"${m.reason}"
                  fbMTableId:"${m.tableId}"
                }`
                )
                .join(",") +
              "]";
          }

          let linesString = "[]";
          if (Array.isArray(o.lines) && o.lines.length > 0 && typeof o.lines[0] === "object") {
            const formattedLines = o.lines.map((line) => {
              const lineMetaString = line.meta
                ? line.meta
                  .map(
                    (m) => `{
                      fbOrderLineMetaId:"${m.fbOrderLineMetaId || ""}"
                      key:"${m.key}"
                      value:${JSON.stringify(m.value)}
                    }`
                  )
                  .join(",")
                : "";

              return `{
              fbOrderId: "${o.fbOrderId}"
              fbOrderLineId: "${line.fbOrderLineId}"
              mPoductId: "${line.mPoductId}"
              mBatchId: ${line.mBatchId ? `"${line.mBatchId}"` : null}
              description: "${line.description || ""}"
              csUomId: "${line.csUomId}"
              csTaxId: "${line.csTaxId}"
              discount:${line.discount ?? 0}
              line:${line.line ?? 0}
              qty:${line.qty ?? 0}
              unitPrice:${line.unitPrice ?? 0}
              listPrice:${line.listPrice ?? 0}
              lineNet:${line.lineNet ?? 0}
              lineTax:${line.lineTax ?? 0}
              lineGross:${line.lineGross ?? 0}
              sOrderLineId:${line.sOrderLineId ? `"${line.sOrderLineId}"` : null}
              isOrdered:"${line.isOrdered || "N"}"
              isCanceled:"${line.isCanceled || "N"}"
              remarks: "${line.remarks || ""}"
              grossStd:${line.grossStd || 0}
              grossUnit:${line.grossUnit || 0}
              lineStd:${line.lineStd || 0}
              netList:${line.netList ?? 0}
              netStd:${line.netStd ?? 0}
              netUnit:${line.netUnit || 0}
              unitTax:${line.unitTax ?? 0}
              meta:[${lineMetaString}]
            }`;
            });

            linesString = `[${formattedLines.join(",")}]`;
          }

          const skipKots = o.fbOrderStatus !== "IP";
          const formattedKots = skipKots ? [] : generateFormattedKOTs(cartDetails, tillData, selectedProductIds);
          const kotsSection = skipKots ? "" : `kots:[${formattedKots.join(",")}]`;

          const paramsInput = {
            query: `mutation {
            upsertFBOrder(order: {
              cSBunitID: "${tillData.tillAccess.csBunit.csBunitId}"
              fbOrderId: "${o.fbOrderId}"
              fbTableId: "${o.cwrFbTableId}"
              fbSectionId: ${o.cwrFbsectionId ? `"${o.cwrFbsectionId}"` : null}
              sOrderId: null
              isCanceled: "${o.isCanceled || "N"}"
              orderNo: null
              date: "${dayjs().format("YYYY-MM-DD HH:mm:ss")}"
              status: "${o.fbOrderStatus}"
              merge: ${mergeString}
              meta: ${metaString}
              lines: ${linesString}
              ${kotsSection}
            }) {
              status
              message
              documentNo
            }
          }`,
          };

          const response = await upsertFBOrder(paramsInput);

          if (response) {
            // Caller handles DB write on success
            useRestaurantStore.getState().setCancelKOTRemarks(null);
            const documentNo = response?.data?.data?.upsertFBOrder?.documentNo;
            if (documentNo) {
              localStorage.setItem("orderId", documentNo);
            }
            tempResponse = response;
          }
        }
    }

    return tempResponse;
  } else if (type === "getFbOrder") {
    const paramsInput = {
      query: `query {
        getFBOrder(
          tableId: "${item.tableId}"
          status: "${item.status}"
        ) {
          fbOrderId
          cSBunitID
          isactive
          sOrderId
          fbTableId
          orderNo
          fbSectionId
          date
          guests
          guestType
          salesRepId
          guestName
          referredBy
          status
          isCanceled
          discAmount
          fbSplitTable
          merge {
            fbMergeId
            fbMergeReason
            fbTableId
          }
          lines {
            fbOrderLineId
            line
            mPoductId
            mBatchId
            description
            csUomId
            qty
            unitPrice
            listPrice
            csTaxId
            lineTax
            lineGross
            isOrdered
            sOrderLineId
            isCanceled
            discount
            mPricingRuleId
            name
            value
            grossStd
            grossUnit
            lineStd
            netList
            netStd
            netUnit
            unitTax
            meta {
              fbOrderLineMetaId
              key
              value
            }
          }
          meta {
            fbOrderMetaId
            key
            value
          }
        }
      }`,
    };

    return await getFBOrder(paramsInput);
  }
};

export { SyncData };
